import { ColumnSource, RowSource } from "@omegagrid/grid";
import { msg } from "@omegagrid/localize";
import rp, { GridEditorAdapter, GridEditorOptions, RowData } from "@reporting/rp-app-host";

/** An OpenID Connect preset as rp-ms-accounting /ssoOidcPresets returns it to a superadmin */
type SsoOidcPreset = {
	id?: number;
	/** the id the row was loaded with (hidden): an edited id would create a second preset, so it is ignored */
	orig_id?: number;
	name?: string;
	base_url?: string;
	client_id?: string;
	settings?: string | null;
	has_secret?: boolean;
	client_secret?: string;
};

/**
 * OpenID Connect presets (SA only): the client of our own multi-tenant application (Microsoft Entra
 * ID) that groups on a shared domain pick in their SSO settings instead of registering one. Stored as
 * P_ACCOUNTING_GROUP rows with a negative id (-1 production, -2 beta - one super db serves both, so
 * each preset names the deployment its redirect URI is registered for in `base_url`). The client
 * secret is write-only: left empty, the stored one stays.
 */
export class SsoPresetGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			data: 'id',
			header: '#',
			t: 'n',
			e: true,
			w: 60,
		}, {
			data: 'name',
			header: msg('Name'),
			t: 't',
			e: true,
			w: 200,
		}, {
			data: 'base_url',
			header: msg('Base URL'),
			t: 't',
			e: true,
			w: 220,
		}, {
			data: 'client_id',
			header: msg('Client ID'),
			t: 't',
			e: true,
			w: 290,
		}, {
			data: 'client_secret',
			header: msg('Client secret'),
			t: 't',
			e: true,
			w: 160,
		}, {
			data: 'has_secret',
			header: msg('Secret set'),
			t: 'b',
			e: false,
			w: 80,
		}, {
			data: 'settings',
			header: msg('Settings'),
			t: 't',
			e: true,
			w: 300,
		}, {
			data: 'redirect_uri',
			header: msg('Redirect URI'),
			t: 't',
			e: false,
			w: 380,
		}, {
			data: 'orig_id',
			t: 'n',
			e: false,
			hidden: true,
		}];
		return this._columns;
	}

	async getRows(): Promise<RowSource[]> {
		const presets = await rp.ms('accounting').get<SsoOidcPreset[]>('ssoOidcPresets', { all: 1 });
		return presets.map(p => ({ data: {
			...p,
			orig_id: p.id,
			client_secret: '',
			redirect_uri: p.base_url ? `${p.base_url}/auth/sso/${p.id}/oidc/callback` : '',
		}}));
	}

	async save(rowData: RowData[]) {
		const data = rowData.map(row => {
			const p = row.data as SsoOidcPreset;
			return {
				id: row.state === 'new' ? (p.id ?? null) : (p.orig_id ?? p.id ?? null),
				name: p.name,
				base_url: p.base_url,
				client_id: p.client_id,
				settings: p.settings ?? '',
				...(p.client_secret ? { client_secret: p.client_secret } : {}),
			};
		});
		await rp.ms('accounting').post('ssoOidcPresets', data);
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			rowNumbers: true,
			columnSorting: false,
			filters: false,
			pageSize: null,
			refreshAfterSave: true,
			saveMode: 'edited_rows',
			toolbar: {
				new: { text: msg('Add preset') },
			},
			rowMenu: {
				addRows: true,
				deleteRows: false,
			},
		});
	}

}
